import { memo, useMemo } from 'react';

import dayjs from 'dayjs';
import { isEmpty } from 'remeda';
import useSWR from 'swr';

import { tournamentsService } from 'v2/api/tournamentsService';
import { Select } from 'v2/components/select/Select';
import { SelectOption } from 'v2/types';
import { TournamentModel } from 'v2/types/TournamentModel';

interface TournamentPickerProps {
  value: string;
  onChange: (tournamentId: string) => void;
}

const NONE_OPTION: SelectOption = { id: 'none', value: '', label: 'All' };

const toOption = ({ id, name, startDate, endDate }: TournamentModel): SelectOption => ({
  id,
  value: id,
  label: `${name} (${dayjs(startDate).format('DD.MM')} – ${dayjs(endDate).format('DD.MM')})`,
});

// The backend returns started tournaments that ended within the transactions retention window, newest first.
export const TournamentPicker = memo<TournamentPickerProps>(({ value, onChange }) => {
  const { data: tournaments } = useSWR('getRecentTournaments', tournamentsService.getRecentTournaments);

  const options = useMemo(() => {
    const recentOptions = (tournaments ?? []).map(toOption);

    // A tournament from the URL may be missing in the list (already older than the window): keep it selectable.
    const isUnknownValue = !isEmpty(value) && !recentOptions.some((option) => option.value === value);

    return [NONE_OPTION, ...recentOptions, ...(isUnknownValue ? [{ id: value, value, label: value }] : [])];
  }, [tournaments, value]);

  return <Select size="small" label="Tournament" value={value} options={options} onChange={onChange} />;
});

TournamentPicker.displayName = 'TournamentPicker';
